// View as the customer (PMU-290): the customer's side. Requests from Vavr support to answer, an
// active view to end, the red banner while support is looking, and the page the one-time link opens.
import { useEffect, useState } from "react";
import { Banner, Button } from "@vavr/ui";
import { api, ApiError, dateTime, useAsync } from "../shared/index.ts";

interface ViewRequest { id: string; kind: "org" | "account"; org_name: string | null; by: string; reason: string; hours: number; state: string; created_at: number; expires_at: number | null }

/** Pending requests (approve or decline) and active views (end now). Nothing when there are none. */
export function SupportAccess() {
  const q = useAsync(() => api<{ requests: ViewRequest[] }>("GET", "/api/v1/support/view-requests").catch(() => ({ requests: [] })), []);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const act = async (id: string, what: "approve" | "decline" | "end") => {
    setBusy(id); setError(null);
    try { await api("POST", `/api/v1/support/view-requests/${id}/${what}`); q.reload(); }
    catch (e) { setError(e instanceof ApiError ? e.message : "Could not reach Vavr."); }
    finally { setBusy(null); }
  };
  const list = (q.data?.requests ?? []).filter((r) => r.state === "pending" || r.state === "active");
  if (!list.length) return null;
  return (
    <div className="pmu-stack">
      {error && <Banner tone="danger" title={error} />}
      {list.map((r) => r.state === "pending" ? (
        <Banner key={r.id} tone="warning" title={`${r.by} at Vavr support asks to view ${r.kind === "org" ? r.org_name ?? "your organization" : "your account"} for ${r.hours} hours`}>
          <p>{`To help with: "${r.reason}". They can see what you see, cannot change anything, and you can end it at any time.`}</p>
          <div className="pmu-row">
            <Button size="sm" loading={busy === r.id} onClick={() => void act(r.id, "approve")}>Allow</Button>
            <Button size="sm" variant="secondary" disabled={busy === r.id} onClick={() => void act(r.id, "decline")}>Decline</Button>
          </div>
        </Banner>
      ) : (
        <Banner key={r.id} tone="info" title={`Vavr support (${r.by}) can view ${r.kind === "org" ? r.org_name ?? "your organization" : "your account"} until ${r.expires_at ? dateTime(r.expires_at) : "later"}`}>
          <Button size="sm" variant="secondary" loading={busy === r.id} onClick={() => void act(r.id, "end")}>End it now</Button>
        </Banner>
      ))}
    </div>
  );
}

/** The red line across every page while an employee is looking (read-only). */
export function ViewingBanner({ view, name }: { view: { by: string | null; expires_at: number } | null | undefined; name: string }) {
  if (!view) return null;
  return <Banner tone="danger" title={`Vavr support${view.by ? ` (${view.by})` : ""} is viewing as ${name}, read-only, until ${dateTime(view.expires_at)}.`} />;
}
/** The same, for screens without the console's data: asks /api/v1/me itself. */
export function ViewingBannerSelf() {
  const q = useAsync(() => api<{ account: { name: string }; view: { by: string | null; expires_at: number } | null }>("GET", "/api/v1/me").catch(() => null), []);
  return q.data?.view ? <ViewingBanner view={q.data.view} name={q.data.account.name} /> : null;
}

/** /view#<one-time token>: starts the read-only session, then opens the console or the parent screens. */
export function ViewStart() {
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const token = location.hash.slice(1);
    history.replaceState(null, "", "/view");
    if (!token) { setError("This link is incomplete. Open it again from Vavr Ops."); return; }
    api<{ next: string }>("POST", "/api/v1/auth/view", { token }).then((r) => location.replace(r.next), (e) => setError(e instanceof ApiError ? e.message : "Could not reach Vavr."));
  }, []);
  return <div className="pmu-page pmu-stack">{error ? <Banner tone="danger" title={error} /> : <p>Opening a read-only view…</p>}</div>;
}
